import {
	getUserOptions,
	editUserOptions,
	UserTypeEnum,
	UserTypeOptions,
	ClinicalTypeOptions,
	getLoggedInUserOptions,
	type FullUserViewModel
} from '@generated';
import Yup from '~/util/yup';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { useNavigate } from 'react-router-dom';
import { AutocompleteSearchFormField } from '~/shared/forms/controls/fields/AutocompleteSearchFormField';
import { CheckboxGroupFormField } from '~/shared/forms/controls/fields/CheckboxGroupFormField';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { RadioGroupFormField } from '~/shared/forms/controls/fields/RadioGroupFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { PhoneNumberFormField } from '~/shared/forms/controls/fields/text-fields/PhoneNumberFormField';
import { Form } from '~/shared/forms/Form';
import { FooterButtonContainer } from '~/shared/forms/buttons/FooterButtonContainer';
import { CancelButton } from '~/shared/forms/buttons/CancelButton';
import { FormSubmitButton } from '~/shared/forms/buttons/FormSubmitButton';
import { IncludedZipCodesFormField } from './zip-codes/IncludedZipCodesFormField';

const validationSchema = Yup.object().shape({
	firstName: Yup.string().label('First Name').nullable().required(),
	lastName: Yup.string().label('Last Name').nullable().required(),
	middleInitial: Yup.string().label('Middle Initial').nullable(),
	emailAddress: Yup.string().label('Email Address').nullable().email(),
	phoneNumber: Yup.string()
		.label('Phone Number')
		.nullable()
		.matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Phone number must be a valid US phone number'),
	primaryUserTypeId: Yup.string().label('Primary User Type').required(),
	additionalUserTypeIds: Yup.array()
		.of(Yup.string())
		.label('Additional User Types')
		.test(
			'no-duplicate-primary',
			'Additional user types cannot include the primary user type',
			(x, y) => !x?.includes(y.parent.primaryUserTypeId)
		),
	clinicalTypeId: Yup.string().label('Clinical Type').nullable(),
	managerId: Yup.string().label('Manager').nullable(),
	loginEnabled: Yup.boolean().label('Login Enabled'),
	chatEnabled: Yup.boolean().label('Chat Enabled'),
	address: Yup.object().address().label('Address').nullable(),
	acceptingReferrals: Yup.boolean().label('Accepting Referrals'),
	includeZipCodes: Yup.array()
		.label('Included Zip Codes')
		.of(
			Yup.string()
				.required('Zip code cannot be empty')
				.matches(/^\d{5}$/, 'Zip code must be a 5 digit number')
		)
});

export function EditUserForm({ user }: { user: FullUserViewModel }) {
	const navigate = useNavigate();
	const { userIsAnAdmin, loggedInUser } = useIdentity();
	const isAnAdmin = userIsAnAdmin();

	const additionalUserTypeIds = user.additionalUserTypes?.map(ut => ut.id) ?? [];

	const filteredUserTypes = isAnAdmin ? UserTypeOptions : UserTypeOptions.filter(ut => ut.id !== UserTypeEnum.Administrator);

	return (
		<Form
			initialValues={{
				...user,
				userId: user.id,
				additionalUserTypeIds,
				clinicalTypeId: user.clinicalTypeId ?? null
			}}
			saveMutation={{
				...editUserOptions,
				invalidateQueryKeys: [getUserOptions(user.id).queryKey, ...(loggedInUser.id !== user.id ? [] : [getLoggedInUserOptions().queryKey])],
				onSuccessMessage: 'User updated successfully'
			}}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to User Library',
				errorAction: () => navigate('/settings/libraries/users')
			}}
			showValues
		>
			{formHelpers => {
				const containsClinicianType =
					Number(formHelpers.values.primaryUserTypeId) === UserTypeEnum.Clinician ||
					formHelpers.values.additionalUserTypeIds.filter((x: string) => Number(x) === UserTypeEnum.Clinician).length > 0;
				return (
					<>
						{formHelpers.displayErrorElement && formHelpers.errorElement}
						<div className="mb-4 grid grid-cols-2 gap-6">
							<div className="col-span-2 grid grid-cols-7 gap-4">
								<TextFormField name="firstName" className="col-span-3" />
								<TextFormField name="middleInitial" />
								<TextFormField name="lastName" className="col-span-3" />
							</div>
							<AutocompleteSearchFormField name="managerId" searchType="user" label="Manager" />
							<TextFormField name="emailAddress" isReadOnly />
							<PhoneNumberFormField name="phoneNumber" />
							<div className="col-span-2">
								<AddressFormField name="address" />
							</div>

							<SelectFormField name="primaryUserTypeId" options={filteredUserTypes} />
							<CheckboxGroupFormField
								name="additionalUserTypeIds"
								options={filteredUserTypes}
								className="col-span-2"
								containerClassName="grid grid-cols-2 gap-4"
							/>
							{containsClinicianType && <RadioGroupFormField name="clinicalTypeId" options={ClinicalTypeOptions} />}
							<CheckboxFormField name="acceptingReferrals" hideLabel className="col-span-2" />
							<IncludedZipCodesFormField
								name="includeZipCodes"
								addressFieldName="address"
								label="Included Zip Codes"
								className="col-span-2"
							/>
						</div>
						<FooterButtonContainer className="border-t border-gray-200 pt-6">
							<CancelButton goBackOnCancel />
							<FormSubmitButton />
						</FooterButtonContainer>
					</>
				);
			}}
		</Form>
	);
}
